import { useState, useEffect, useCallback } from "react"
import { Button, Form, Alert, Card } from "react-bootstrap"
import { StageCard } from "./StageCard"
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay } from "@dnd-kit/core"
import { SortableContext, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable"
import { arrayMoveImmutable } from "array-move"
import { CSS } from "@dnd-kit/utilities"
import { IconPlus, IconGripVertical, IconDots, IconCopy, IconTrash, IconGitMerge } from "@tabler/icons-react"
import useLocalStorageState from "use-local-storage-state"
import Tooltip from "@mui/material/Tooltip"
import DeleteModal from "../DeleteModal"
import { toast } from "sonner"

// Utility function to normalize stage names for comparison
const normalizeStageNameForComparison = (name) => {
  return name.trim().replace(/\s+/g, " ").toLowerCase()
}

// Utility function to clean stage name for sending (remove extra spaces but preserve case)
const cleanStageNameForSending = (name) => {
  return name.trim().replace(/\s+/g, " ")
}

const SortableStage = ({ id, children }) => {
  // Simple functionality: Sortable stage wrapper component
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="stage-wrapper">
      {children({ listeners })}
    </div>
  )
}

export const PipelineEditor = ({
  pipelineName: initialPipelineName,
  pipelineStage,
  onClose,
  onSave,
  onDuplicate,
  onDeletePipeline,
  onStageDeleteRequest,
  save,
  pipelineCount,
  isCreatingNewPipeline,
  isDuplicatingPipeline = false,
}) => {
  // Simple functionality: Pipeline editor with drag and drop stages
  const [localStages, setLocalStages] = useState([])
  const [localPipelineName, setLocalPipelineName] = useState(initialPipelineName)
  const [activeId, setActiveId] = useState(null)
  const [showWarning, setShowWarning] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [originalData, setOriginalData] = useState(null)

  const [stageToDelete, setStageToDelete] = useState(null)

  // Add this state at the top of PipelineEditor
  const [isInitialized, setIsInitialized] = useState(false)

  // Replace the existing initialization useEffect with this:
  useEffect(() => {
    // Only initialize once when component first mounts or when explicitly needed
    if (isInitialized) return

    if (isCreatingNewPipeline && !isDuplicatingPipeline) {
      const newPipelineData = {
        pipelineName: "New Pipeline",
        stages: [
          {
            id: `new-${Date.now()}-1`,
            stage_name: "Enquiry",
            sequence: "1",
            probability: 0,
            rotting_days: null,
            rotting_enabled: false,
            isNew: true,
            isTemporary: true,
            hasChanges: true,
          },
          {
            id: `new-${Date.now()}-2`,
            stage_name: "In Progress",
            sequence: "2",
            probability: 0,
            rotting_days: null,
            rotting_enabled: false,
            isNew: true,
            isTemporary: true,
            hasChanges: true,
          },
          {
            id: `new-${Date.now()}-3`,
            stage_name: "Perspective",
            sequence: "3",
            probability: 0,
            rotting_days: null,
            rotting_enabled: false,
            isNew: true,
            isTemporary: true,
            hasChanges: true,
          },
          {
            id: `new-${Date.now()}-4`,
            stage_name: "Won",
            sequence: "4",
            probability: 0,
            rotting_days: null,
            rotting_enabled: false,
            isNew: true,
            isTemporary: true,
            hasChanges: true,
          },
        ],
      }

      setLocalPipelineName(newPipelineData.pipelineName)
      setLocalStages(newPipelineData.stages)
      setOriginalData(newPipelineData)
      setHasChanges(true)
      setIsInitialized(true)
    } else if (pipelineStage && pipelineStage.length > 0 && pipelineStage !== null) {
      const formattedStages = pipelineStage.map((stage, index) => ({
        ...stage,
        id: stage.id || `stage-${Date.now()}-${index}`,
        probability: Math.min(100, stage.probability || 0),
        rotting_days: stage.rotting_days === null ? null : stage.rotting_days,
        rotting_enabled: stage.rotting_days !== null && stage.rotting_days > 0,
        isNew: isDuplicatingPipeline,
        isTemporary: isDuplicatingPipeline,
        hasChanges: isDuplicatingPipeline,
        originalData: { ...stage },
      }))

      const currentData = {
        pipelineName: initialPipelineName,
        stages: formattedStages,
      }

      setLocalStages(formattedStages)
      setLocalPipelineName(initialPipelineName)
      setOriginalData(currentData)
      setHasChanges(isDuplicatingPipeline)
      setIsInitialized(true)
    }
  }, []) // Empty dependency array - only run once on mount

  console.log("localStages", localStages)
  // Check if there are actual changes
  const checkForChanges = useCallback(() => {
    if (!originalData) return false

    // Check pipeline name change
    if (localPipelineName !== originalData.pipelineName) {
      return true
    }

    // Check stages changes
    const visibleStages = localStages.filter((stage) => stage.action !== "Delete")
    const originalStages = originalData.stages.filter((stage) => stage.action !== "Delete")

    if (visibleStages.length !== originalStages.length) {
      return true
    }

    // Check individual stage changes
    for (let i = 0; i < visibleStages.length; i++) {
      const current = visibleStages[i]
      const original = originalStages.find((s) => (s.stage_id || s.id) === (current.stage_id || current.id))

      if (!original) return true // New stage

      if (
        current.stage_name !== original.stage_name ||
        current.sequence !== original.sequence ||
        current.probability !== original.probability ||
        current.rotting_days !== original.rotting_days ||
        current.rotting_enabled !== original.rotting_enabled
      ) {
        return true
      }
    }

    return false
  }, [localPipelineName, localStages, originalData])

  // Check for duplicate stage names and update error states
  const updateDuplicateErrors = useCallback(() => {
    const visibleStages = localStages.filter((stage) => stage.action !== "Delete")
    const stageNameCounts = new Map()
    const duplicateNormalizedNames = new Set()

    // Count occurrences of normalized stage names
    visibleStages.forEach((stage) => {
      const normalizedName = normalizeStageNameForComparison(stage.stage_name)
      if (normalizedName) {
        const count = stageNameCounts.get(normalizedName) || 0
        stageNameCounts.set(normalizedName, count + 1)

        if (count >= 1) {
          duplicateNormalizedNames.add(normalizedName)
        }
      }
    })

    // Update stages with error messages
    setLocalStages((prev) =>
      prev.map((stage) => {
        if (stage.action === "Delete") return stage

        const normalizedName = normalizeStageNameForComparison(stage.stage_name)
        const isDuplicate = normalizedName && duplicateNormalizedNames.has(normalizedName)

        return {
          ...stage,
          nameError: isDuplicate ? "Stage name already exists" : "",
        }
      }),
    )

    return duplicateNormalizedNames
  }, [localStages])

  // Update hasChanges when data changes
  useEffect(() => {
    setHasChanges(checkForChanges())
  }, [checkForChanges])

  // Update duplicate errors when stages change
  useEffect(() => {
    updateDuplicateErrors()
  }, [localStages.map((s) => s.stage_name).join("|")])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  // Handle drag start event for stage reordering
  const handleDragStart = useCallback((event) => {
    // Simple functionality: Start drag operation
    setActiveId(event.active.id)
  }, [])

  // Handle drag end event and update stage sequences
  const handleDragEnd = useCallback((event) => {
    // Simple functionality: End drag operation and reorder stages
    const { active, over } = event
    setActiveId(null)

    if (!over || active.id === over.id) {
      return
    }

    setLocalStages((items) => {
      const visibleItems = items.filter((item) => item.action !== "Delete")
      const oldIndex = visibleItems.findIndex((item) => item.id === active.id)
      const newIndex = visibleItems.findIndex((item) => item.id === over.id)

      if (oldIndex === -1 || newIndex === -1) return items

      const newVisibleItems = arrayMoveImmutable(visibleItems, oldIndex, newIndex)
      const updatedVisibleItems = newVisibleItems.map((item, index) => {
        const updatedItem = {
          ...item,
          sequence: String(index + 1),
        }

        if (!item.isNew && item.sequence !== String(index + 1)) {
          updatedItem.hasChanges = true
        }

        return updatedItem
      })

      const deletedItems = items.filter((item) => item.action === "Delete")
      const allItems = [...updatedVisibleItems, ...deletedItems]

      return allItems
    })
  }, [])

  // Update a specific field for a stage and mark as changed
  const updateStageField = (stageId, field, value, color) => {
    setLocalStages((prev) =>
      prev.map((stage) => {
        if ((stage.stage_id || stage.id) === stageId) {
          let processedValue = value

          if (field === "probability") {
            processedValue = Math.min(100, Math.max(0, value))
          }

          const updatedStage = { ...stage, [field]: processedValue }

          // Handle color update
          if (color !== undefined) {
            updatedStage.color = color
          }

          if (!stage.isNew) {
            const originalValue = stage.originalData?.[field]
            if (originalValue !== processedValue) {
              updatedStage.hasChanges = true
            }
          } else {
            updatedStage.hasChanges = true
          }

          return updatedStage
        }
        return stage
      }),
    )
  }

  // Mark a stage for deletion - Handle temporary vs saved stages differently
  const deleteStage = async (stageId) => {
    console.log("stageId", stageId)
    const visibleStages = localStages.filter((stage) => stage.action !== "Delete")
    if (visibleStages.length <= 1) {
      setShowWarning(true)
      return
    }

    const stageToDelete = localStages.find((stage) => (stage.id) === stageId)

    // If it's a temporary stage (newly created and not saved), remove it directly
    if (stageToDelete?.isTemporary || stageToDelete?.isNew) {
      setLocalStages((prev) => {
        const filteredStages = prev.filter((stage) => (stage.stage_id || stage.id) !== stageId)
        // Resequence remaining stages
        const visibleStages = filteredStages.filter((stage) => stage.action !== "Delete")
        const resequencedStages = visibleStages.map((stage, index) => ({
          ...stage,
          sequence: String(index + 1),
        }))
        const deletedStages = filteredStages.filter((stage) => stage.action === "Delete")
        return [...resequencedStages, ...deletedStages]
      })
    } else {
      // For saved stages, use the API deletion process
      const res = await onStageDeleteRequest(stageId)
      // if (res) {
      setStageToDelete(null)
      // }
    }
  }

  useEffect(() => {
    if (pipelineStage && pipelineStage.length > 0) {
      // Format the stages when they come from parent
      const formattedStages = pipelineStage.map((stage, index) => ({
        ...stage,
        id: stage.id || stage.stage_id || `stage-${Date.now()}-${index}`,
        probability: Math.min(100, stage.probability || 0),
        rotting_days: stage.rotting_days === null ? null : stage.rotting_days,
        rotting_enabled: stage.rotting_days !== null && stage.rotting_days > 0,
        isNew: false,
        isTemporary: false,
        hasChanges: false,
        originalData: { ...stage },
      }))

      setLocalStages(formattedStages)
    }
  }, [pipelineStage]) // Only depend on pipelineStage

  // Add a new stage at specified position (left or right of reference)
  const addStageAt = (referenceSequence, direction) => {
    // Simple functionality: Add new stage at specified position
    const visibleStages = localStages.filter((stage) => stage.action !== "Delete")
    let insertIndex

    if (direction === "left") {
      insertIndex = referenceSequence - 1
    } else {
      insertIndex = referenceSequence
    }

    insertIndex = Math.max(0, Math.min(insertIndex, visibleStages.length))

    const newStage = {
      id: `new-${Date.now()}-${Math.random()}`,
      stage_name: "New Stage",
      probability: 0,
      rotting_enabled: false,
      rotting_days: null,
      sequence: String(insertIndex + 1),
      isNew: true,
      isTemporary: true, // Mark as temporary
      hasChanges: true,
    }

    setLocalStages((prev) => {
      const visibleStages = prev.filter((stage) => stage.action !== "Delete")
      const newStages = [...visibleStages]
      newStages.splice(insertIndex, 0, newStage)

      const updatedStages = newStages.map((stage, index) => ({
        ...stage,
        sequence: String(index + 1),
      }))

      const deletedItems = prev.filter((stage) => stage.action === "Delete")
      const allStages = [...updatedStages, ...deletedItems]

      return allStages
    })
  }
  // Save pipeline changes and send to parent component
  const handleSave = () => {
    console.log("saving")
    // Simple functionality: Save pipeline changes
    const visibleStages = localStages.filter((stage) => stage.action !== "Delete")

    if (visibleStages.length === 0) {
      setShowWarning(true)
      return
    }

    // Check for duplicate stage names
    const duplicateNames = updateDuplicateErrors()
    // return
    if (duplicateNames.size > 0) {
      toast.warning("Stage name already exists. Each stage must have a unique name.")
      return
    }
    const payload = {
      pipeline_name: localPipelineName.trim(),
      pipeline_stage: localStages.map((stage) => {
        const baseStage = {
          stage_id: stage.stage_id || (stage.id && !stage.id.startsWith("new-") ? stage.id : ""),
          stage_name: cleanStageNameForSending(stage.stage_name), // Clean but preserve case
          sequence: String(stage.sequence),
          color: stage.color ? stage.color : "#ffffff"
          // probability: Math.min(100, stage.probability),
        }

        if (stage.rotting_enabled && stage.rotting_days) {
          baseStage.rotting_days = stage.rotting_days
        }

        if (stage.isNew) {
          return { ...baseStage }
        } else if (stage.action === "Delete") {
          return { ...baseStage, action: "Delete" }
        }
        return baseStage
      }),
    }
    // Pass the actual changes flag to the parent
    onSave(payload, hasChanges)
  }

  // Handle cancel action - Direct close without confirmation
  const handleCancel = () => {
    onClose()
  }

  // Handle duplicate pipeline button click - Call onDuplicate function
  const handleDuplicatePipeline = () => {
    onDuplicate()
  }

  // Update pipeline name and mark as changed
  const handlePipelineNameChange = (value) => {
    // Simple functionality: Update pipeline name
    setLocalPipelineName(value)
  }

  const visibleStages = localStages.filter((stage) => stage.action !== "Delete")


  const tooltipStyles = {
    tooltip: {
      sx: {
        fontSize: '0.8rem',
        backgroundColor: "#7a3a5e",
        color: "white",
      }
    },
    arrow: {
      sx: {
        color: "#7a3a5e",
      }
    }
  };

  return (
    <div className="pipeline-editor-container bg-white p-4" style={{ minHeight: "75vh" }}>
      {/* Header */}
      {/* Professional Header Section */}
      <div className="pipeline-editor-header mb-3 px-1">
        <div className="d-flex justify-content-between align-items-center">

          {/* Left Side: Context & Title */}
          {/* Left Side: Context & Title */}
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-3">
              {/* The New "Pro" Icon Container */}
              <div
                className="d-flex align-items-center justify-content-center rounded-2"
                style={{
                  background: "linear-gradient(135deg, #7a3a5e 0%, #4f243c 100%)",
                  width: "36px",
                  height: "36px",
                  boxShadow: "0 4px 10px rgba(122, 58, 94, 0.2)"
                }}
              >
                <IconGitMerge size={20} color={'#fff'} />
              </div>
              <div className="d-flex flex-column">
                <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "0.95rem" }}>
                  Pipeline Editor
                </h6>
                <span className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                  Design and order your pipeline stages
                </span>
              </div>
            </div>

            {/* Input Group with Label */}
            <div className="pipeline-input-group" style={{ maxWidth: "500px" }}>
              <label
                htmlFor="pipelineName"
                className="d-block mb-1 ps-1 fw-bold text-muted"
                style={{ fontSize: "0.7rem", letterSpacing: "0.5px", textTransform: "uppercase" }}
              >
                Pipeline Name
              </label>
              <Form.Control
                id="pipelineName"
                type="text"
                value={localPipelineName}
                onChange={(e) => handlePipelineNameChange(e.target.value)}
                placeholder="e.g. Real Estate Sales Flow"
                className="pipeline-modern-input py-2 px-3 shadow-sm"
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  color: "#1e293b",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease-in-out"
                }}
              />
            </div>
          </div>

          {/* Right Side: Action Bar */}
          <div className="d-flex align-items-center gap-3 mt-auto">

            {/* Management Actions with Subtle Colors */}
            {!isCreatingNewPipeline && !isDuplicatingPipeline && (
              <div className="d-flex align-items-center gap-2 border-end pe-3 me-1">
                <Tooltip title="Duplicate Pipeline" arrow placement="bottom" componentsProps={tooltipStyles}>
                  <Button
                    variant="light"
                    className="d-flex align-items-center justify-content-center rounded-3 shadow-sm p-2"
                    style={{ backgroundColor: "#f0f7ff", border: "1px solid #e0eefe" }}
                    onClick={handleDuplicatePipeline}
                    title="Duplicate Pipeline"
                  >
                    <IconCopy size={19} stroke={2} color="#0066ff" />
                  </Button>
                </Tooltip>

                {pipelineCount > 1 && (
                  <Tooltip title="Delete Pipeline" arrow placement="bottom" componentsProps={tooltipStyles}>
                    <Button
                      variant="light"
                      className="d-flex align-items-center justify-content-center rounded-3 shadow-sm p-2"
                      style={{ backgroundColor: "#fff5f5", border: "1px solid #ffebeb" }}
                      onClick={onDeletePipeline}
                      title="Delete Pipeline"
                    >
                      <IconTrash size={19} stroke={2} color="#e03131" />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}

            {/* Main Controls */}
            {/* Main Controls */}
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="link"
                onClick={handleCancel}
                // {/* Added 'discard-btn' class for specific hover logic */}
                className="text-decoration-none discard-btn px-3 fw-semibold"
                style={{
                  backgroundColor: "#ffffff",
                  color: "#7a3a5e",
                  border: "1px solid #7a3a5e",
                  fontSize: "14px",
                  borderRadius: "5px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                }}
              >
                Discard
              </Button>

              <button
                onClick={handleSave}
                className="save-pipeline-btn px-4 py-2 d-flex align-items-center gap-2 fw-bold"
                disabled={save}
                style={{
                  backgroundColor: save ? "#6c3152" : "#7a3a5e",
                  color: "#fff",
                  border: "1px solid #7a3a5e",
                  fontSize: "14px",
                  borderRadius: "5px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                }}
              >
                {isCreatingNewPipeline ? <IconPlus size={18} stroke={3} /> : null}
                {isCreatingNewPipeline ? "Create Pipeline" : "Save Changes"}
                {save && <span className="spinner-border spinner-border-sm"></span>}
              </button>
            </div>
          </div>
        </div>
      </div>
      <hr style={{ margin: 0 }} />
      <style jsx>
        {`
  /* Input Focus State */
  .pipeline-modern-input:focus {
    border-color: #7a3a5e !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 4px rgba(122, 58, 94, 0.1) !important;
    outline: none;
  }

  /* Discard Button: Fill background so white text is visible */
  .discard-btn:hover {
    background-color: #7a3a5e !important;
    color: #ffffff !important; 
    text-decoration: none;
  }

  /* Save Changes Button: Transition to the darker shade */
  .save-pipeline-btn:hover {
    background-color: #6c3152 !important;
    border-color: #4f243c !important;
    color: #ffffff !important;
    box-shadow: 0 4px 12px rgba(79, 36, 60, 0.35) !important;
  }

  /* Management Icon Hover Effects */
  .btn-light:hover {
    filter: brightness(0.98);
    transform: scale(1.05);
    transition: transform 0.2s ease;
  }
`}
      </style>

      {/* Warning Alert */}
      {showWarning && (
        <Alert variant="warning" onClose={() => setShowWarning(false)} dismissible className="mb-4">
          Your pipeline must have at least one stage.
        </Alert>
      )}

      {/* Stages Editor */}
      <div className="stages-editor">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={visibleStages.map((stage) => stage.id)} strategy={horizontalListSortingStrategy}>
            <div
              className="d-flex gap-1 stages-list pb-3"
              style={{
                overflowX: "auto",
                overflowY: "visible",
                minHeight: "400px",
                scrollbarWidth: "thin",
                scrollbarColor: "#e0e0e0 #f1f1f1",
              }}
            >
              <style>
                {`
                .stages-list::-webkit-scrollbar {
                  height: 8px;
                }
                .stages-list::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 4px;
                }
                .stages-list::-webkit-scrollbar-thumb {
                  background: #7a3a5e;
                  border-radius: 4px;
                }
                .stages-list::-webkit-scrollbar-thumb:hover {
                  background: #5a2a4e;
                }
                `}
              </style>
              {visibleStages.map((stage) => (
                <SortableStage key={stage.id} id={stage.id}>
                  {({ listeners }) => (
                    <div style={{ minWidth: "250px" }}>
                      <StageCard
                        stage={stage}
                        listeners={listeners}
                        isEditing={true}
                        onChangeField={updateStageField}
                        onDelete={deleteStage}
                        onAddLeft={() => addStageAt(Number.parseInt(stage.sequence), "left")}
                        onRequestDelete={(id) => setStageToDelete(id)}
                        onAddRight={() => addStageAt(Number.parseInt(stage.sequence), "right")}
                      />
                    </div>
                  )}
                </SortableStage>
              ))}

              {/* Add New Stage Button */}
              <div className="add-new-stage-col" style={{ minWidth: "250px", marginLeft: "5px" }}>
                <Card
                  className="h-100 border-1 border-dashed d-flex align-items-center justify-content-center"
                  style={{
                    borderColor: "#7a3a5e",
                    maxHeight: "418px",
                    marginTop: "1rem"
                  }}
                >
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <Button
                      className="d-flex align-items-center btn btn-default-outline gap-2"
                      onClick={() => addStageAt(visibleStages.length, "right")}
                      style={{ borderColor: "#7a3a5e", color: "#7a3a5e" }}
                    >
                      <IconPlus size={18} />
                      Add Stage
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </SortableContext>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeId ? (
              <Card className="shadow-lg" style={{ width: "250px", opacity: 0.9 }}>
                <Card.Header className="d-flex justify-content-between align-items-center bg-light p-3">
                  <span className="fw-bold">{visibleStages.find((s) => s.id === activeId)?.stage_name}</span>
                  <IconGripVertical size={16} />
                </Card.Header>
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Probability</span>
                    <span className="fw-bold" style={{ color: "#7a3a5e" }}>
                      {visibleStages.find((s) => s.id === activeId)?.probability || 0}%
                    </span>
                  </div>
                </Card.Body>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 999 }}
          onClick={() => setShowDropdown(false)}
        />
      )}
      <p className=" mb-2" style={{ height: "5rem" }}>
        <div style={{}}>
          <div className="alert alert-warning ">
            <div className="d-flex align-items-start gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: "36px",
                  height: "36px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #f0ad4e",
                  flexShrink: 0,
                }}
              >
                <i
                  className="bi bi-diagram-3"
                  style={{ fontSize: "18px", color: "#8a6d3b" }}
                ></i>
              </div>

              <div>
                <p className="fw-semibold mb-1">
                  How stages work
                </p>
                <p className="mb-1 text-muted">
                  Stages represent each step in your pipeline. Items move forward as they progress.
                </p>
                <p className="mb-0 text-muted small">
                  Tip: Use short, action‑based names for clarity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </p>
      {stageToDelete && (
        <DeleteModal
          show
          onClose={() => setStageToDelete(null)}
          onConfirm={() => {
            deleteStage(stageToDelete)
          }}
          message="Once deleted, this stage can't be recovered..."
        />
      )}
    </div>
  )
}